import { inject, Injectable } from '@angular/core';
import { ApiService } from '../../services/api.service';
import { RehearsalScene } from './rehearsal-scene';
import { Rehearsal } from './rehearsal';
import { RehearsalRequestDTO } from './RehearsalRequestDTO';
import { AuthStateService } from '../auth/auth-state.service';

@Injectable({
    providedIn: 'root'
})
export class RehearsalService {

    private readonly api: ApiService = inject(ApiService);
    private readonly authState: AuthStateService = inject(AuthStateService);

    public async getAll(): Promise<any> {
        const showId = this.authState.activeShowId;
        if (showId == null) {
            throw new Error('RehearsalService.getAll: no active show selected');
        }
        try {
            // Scenes/items are per-show; use the active show, not a hardcoded id
            // (a hardcoded show 1 made rehearsals for every other show list show 1's scenes).
            const response = await this.api
                .getAll<any>(`shows/${showId}/scenes/items`);

            const objects = response.map(item => new RehearsalScene().create(item));
            objects.sort((a, b) => a.orderIndex - b.orderIndex);
            return objects;

        } catch (error) {
            console.error(`Unable to find rehearsal items`);
            throw error;
        }
    }

    public async getByRehearsalId(rehearsalId: number): Promise<Rehearsal> {
        try {
            const response = await this.api
                .getById<Rehearsal>('rehearsals', rehearsalId);
            return new Rehearsal().create(response);
        } catch (error) {
            console.error(`Unable to load Rehearsal with id ${ rehearsalId }`);
            throw error;
        }
        return null;
    }

    public async getByRehearsalToken(rehearsalToken: string): Promise<Rehearsal> {
        try {
            const response = await this.api
                .getAll<Rehearsal>(`rehearsals?token=${rehearsalToken}`);
            return new Rehearsal().create(response);
        } catch (error) {
            console.error(`Unable to load Rehearsal for token ${ rehearsalToken }`);
            throw error;
        }
        return null;
    }

    /**
     * Load everything the external rehearsal page needs in a single call.
     * The endpoint is public (token-guarded) so this works for
     * unauthenticated resident directors who only have the share link.
     */
    public async getExternalRehearsalData(rehearsalToken: string): Promise<{ rehearsal: any; scenes: any[] }> {
        try {
            const response = await this.api.getAll<any>(
                `rehearsals/external/${rehearsalToken}`
            );
            return response as unknown as { rehearsal: any; scenes: any[] };
        } catch (error) {
            console.error(`Unable to load external rehearsal for token ${ rehearsalToken }`);
            throw error;
        }
    }

    public async save(rehearsalRequestDTO: RehearsalRequestDTO): Promise<Rehearsal> {
        try {
            const response = await this.api
                .postRequest('rehearsals', rehearsalRequestDTO);
            return new Rehearsal().create(response);
        } catch (error) {
            console.error(`Unable to create Rehearsal`);
            throw error;
        }
        return null;
    }

    public async requestExternalRehearsalUrlForPerformance(performanceId: number): Promise<any> {
        try {
            const response = await this.api
                .getWithoutData(`rehearsals/performance/${performanceId}/token`);
            return response;
        } catch (error) {
            console.error(`Unable to load Rehearsal for performanceId ${ performanceId }`);
            throw error;
        }
    }

    /**
     * Update an existing rehearsal by its ID (for authenticated users)
     *
     * @param rehearsalId The ID of the rehearsal to update
     * @param rehearsalRequestDTO The updated rehearsal data
     * @returns The updated Rehearsal object
     */
    public async updateRehearsalById(rehearsalId: number, rehearsalRequestDTO: RehearsalRequestDTO): Promise<Rehearsal> {
        try {
            const response = await this.api.put(`rehearsals/${rehearsalId}`, rehearsalRequestDTO);
            return new Rehearsal().create(response);
        } catch (error) {
            console.error(`Unable to update Rehearsal with id ${rehearsalId}`);
            throw error;
        }
    }

    /**
     * Update an existing rehearsal by its token (for unauthenticated users)
     *
     * @param token The rehearsal token
     * @param rehearsalRequestDTO The updated rehearsal data
     * @returns The updated Rehearsal object
     */
    public async updateRehearsalByToken(token: string, rehearsalRequestDTO: RehearsalRequestDTO): Promise<Rehearsal> {
        try {
            const response = await this.api.put(`rehearsals?token=${token}`, rehearsalRequestDTO);
            return new Rehearsal().create(response);
        } catch (error) {
            console.error(`Unable to update Rehearsal with token ${token}`);
            throw error;
        }
    }
}
